import type { ZudokuConfig } from "zudoku";
import { LandingPage } from "zudoku/components";
import HeaderIcon from "./components/header-icon";
import HotjarPlugin from "./plugins/hotjar";

const config: ZudokuConfig = {
  search: {
    type: "pagefind",
  },
  site: {
    title: "Vulavula Documentation",
    logo: {
      src: {
        light: "/lelapa-logo.png",
        dark: "/lelapa-logo-dark.png",
      },
      alt: "Vulavula Logo",
      width: "100px",
      href: "https://lelapa.ai",
    },
    showPoweredBy: false,
    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} Lelapa AI. All rights reserved.`,
    },
  },
  canonicalUrlOrigin: "https://docs.lelapa.ai",
  metadata: {
    title: "%s - Vulavula Documentation",
    description: "Documentation for Vulavula by Lelapa AI",
    keywords: ["speech-to-text", "api", "documentation"],
    favicon: "/favicon.ico",
  },
  navigation: [
    {
      type: "custom-page",
      path: "/",
      element: (
        <LandingPage
          variant="grid"
          eyebrow="Vulavula"
          title="African language AI, built for developers"
          description="Speech-to-text and translation for isiZulu, Sesotho, Afrikaans, South African English, African French and more — with the same calm developer experience whether you call our managed API or run the stack in your own cluster."
          actions={[
            { label: "Quickstart", href: "/overview/introduction" },
            { label: "API Reference", href: "/api" },
            { label: "Self-host (K8s/Openshift)", href: "/integrations/self-deploy" },
          ]}
          features={[
            {
              title: "Quickstart",
              description:
                "Sign up, grab an API key, and ship your first transcription or translation in minutes — every account gets 100 free calls a month, no credit card required.",
              href: "/overview/introduction",
            },
            {
              title: "API Reference",
              description:
                "Full OpenAPI spec for every endpoint — request/response schemas, headers, and copy-paste cURL and Python examples side by side.",
              href: "/api",
            },
            {
              title: "Self-host (K8s/Openshift)",
              description:
                "Run the full inference stack inside your own cluster with the your-cloud Helm chart. Enterprise path — reach out first so we can size and license it with you.",
              href: "/integrations/self-deploy",
            },
          ]}
        />
      ),
    },
    {
      type: "category",
      label: "Documentation",
      icon: "book",
      items: [
        {
          type: "category",
          label: "Overview",
          icon: "sparkles",
          items: [
            "overview/introduction",
            "integrations/authentication",
            "overview/plans",
            "overview/language-support",
            "overview/code-examples",
          ],
        },
        {
          type: "category",
          label: "Integrations",
          icon: "layout-panel-left",
          items: [
            "integrations/sdk",
            "integrations/zapier",
            "integrations/turn-io",
            "integrations/self-deploy",
          ],
        },
        {
          type: "category",
          label: "Speech To Text (STT)",
          icon: "captions",
          items: [
            "transcribe/sync",
            "transcribe/sdk",
            {
              type: "category",
              label: "Model Cards",
              items: [
                "transcribe/model/isizulu-sesotho",
                "transcribe/model/rsa-english",
                "transcribe/model/afrikaans",
                "transcribe/model/african-french",
                "transcribe/model/diarisation",
                "transcribe/model/languageID",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Translate",
          icon: "languages",
          items: [
            "translate/translate",
            {
              type: "category",
              label: "Model Cards",
              items: ["translate/model/translate"],
            },
          ],
        },
        {
          type: "category",
          label: "Live API",
          icon: "radio",
          items: ["live/realtime"],
        },
      ],
    },
    {
      type: "link",
      label: "Release Notes",
      icon: "sticky-note",
      to: "/overview/release-notes",
    },
    {
      type: "link",
      to: "/api",
      label: "API Reference",
      icon: "code",
      badge: {
        label: "v2.0",
        color: "blue",
      },
      display: "always",
    },
  ],
  defaults: {
    apis: {
      examplesLanguage: "python",
      disablePlayground: true,
    },
  },
  catalogs: {
    path: "/api",
    label: "API Reference",
    items: ["api/transcribe", "api/translate"]
  },
  apis: [
    {
      type: "file",
      input: "apis/transcribe.json",
      path: "/api/transcribe",
      categories: [{ label: "", tags: ["Transcription"] }]
    },
    {
      type: "file",
      input: "apis/translate.json",
      path: "/api/translate",
      categories: [{ label: "", tags: ["Translate"] }]
    },
  ],
  redirects: [
    { from: "/vulavula-api/get-started", to: "/overview/introduction" },
    { from: "/overview/quickstart-tutorials", to: "/overview/introduction" },
    {
      from: "/vulavula-api/authentication",
      to: "/integrations/authentication",
    },
    { from: "/vulavula-api/sdk", to: "/integrations/sdk" },
    {
      from: "/getting-started/quickstart-tutorials",
      to: "/overview/introduction",
    },
    {
      from: "/getting-started/language-support",
      to: "/overview/language-support",
    },
    { from: "/getting-started/plans", to: "/overview/plans" },
    // Transcribe
    { from: "/features/Transcribe/", to: "/transcribe/sync" },
    { from: "/category/transcribe", to: "/transcribe/sync" },
    // Translate
    { from: "/features/Translate/", to: "/translate/translate" },
    { from: "/category/translate", to: "/translate/translate" },
    // Sunsetted products (Analyse, Converse, Knowledge Base)
    { from: "/features/Analyse/", to: "/overview/introduction" },
    { from: "/features/Analyse/ner", to: "/overview/introduction" },
    { from: "/category/analyse", to: "/overview/introduction" },
    { from: "/features/Converse/", to: "/overview/introduction" },
    { from: "/category/converse", to: "/overview/introduction" },
    { from: "features/Knowledge%20Base/search", to: "/overview/introduction" },
    { from: "/category/knowledge-base", to: "/overview/introduction" },
  ],
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  slots: {
    "head-navigation-end": HeaderIcon,
  },
  plugins: [HotjarPlugin],
  theme: {
    light: {
      background: "0 0% 100%",
      foreground: "20 14.3% 4.1%",
      card: "0 0% 100%",
      cardForeground: "20 14.3% 4.1%",
      popover: "0 0% 100%",
      popoverForeground: "20 14.3% 4.1%",
      primary: "47 100% 49%",
      primaryForeground: "26 83.3% 14.1%",
      secondary: "60 4.8% 95.9%",
      secondaryForeground: "24 9.8% 10%",
      muted: "60 4.8% 95.9%",
      mutedForeground: "25 5.3% 44.7%",
      accent: "60 4.8% 95.9%",
      accentForeground: "24 9.8% 10%",
      destructive: "0 84.2% 60.2%",
      destructiveForeground: "60 9.1% 97.8%",
      border: "20 5.9% 90%",
      input: "20 5.9% 90%",
      ring: "20 14.3% 4.1%",
    },
    dark: {
      background: "20 14.3% 4.1%",
      foreground: "60 9.1% 97.8%",
      card: "20 14.3% 4.1%",
      cardForeground: "60 9.1% 97.8%",
      popover: "20 14.3% 4.1%",
      popoverForeground: "60 9.1% 97.8%",
      primary: "47.9 95.8% 53.1%",
      primaryForeground: "26 83.3% 14.1%",
      secondary: "12 6.5% 15.1%",
      secondaryForeground: "60 9.1% 97.8%",
      muted: "12 6.5% 15.1%",
      mutedForeground: "24 5.4% 63.9%",
      accent: "12 6.5% 15.1%",
      accentForeground: "60 9.1% 97.8%",
      destructive: "0 62.8% 30.6%",
      destructiveForeground: "60 9.1% 97.8%",
      border: "12 6.5% 15.1%",
      input: "12 6.5% 15.1%",
      ring: "35.5 91.7% 32.9%",
    },
  },
};

export default config;
